import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Suspense, Component } from "react";
import { lazyRoute } from "@/lib/lazyRoute";
import type { ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { getWorkspaceLandingPath, useWorkspaceAudience } from "@/contexts/WorkspaceAudienceContext";
import type { SectionId } from "@/config/uiConfig";
import { resolveRouteAccess } from "@/auth/authorization";
import {
  isBlockingAuthentication,
  pathOf,
  returnToFromState,
  safeReturnTo,
  type ReturnState,
} from "@/auth/session";
import { AccountLoadError } from "@/components/AccountLoadError";
import { ScrollToTop } from "@/hooks/useScrollRestoration";
import LoginPage from "@/pages/LoginPage";
import { DocumentTitle, useDocumentTitle } from "@/hooks/useDocumentTitle";
import DashboardListPage from "@/pages/workspace/DashboardListPage";
import { DashboardRouteState } from "@/components/dashboard/DashboardRouteState";
import TenantListPage from "./pages/admin/TenantListPage";
import { useAccount } from "@/hooks/useAccount";
import { useAccountSetup } from "@/hooks/useAccountSetup";
import { useAuthorizedNavGroups } from "@/components/layout/NavSidebar";
import { FullPageLoader, FullPageState } from "@/components/FullPageState";
import HomePage from "@/pages/home/HomePage";
import { DEFAULT_REPORT_ID } from "@/pages/measurement/reportsCatalog";
import {
  LEGACY_LIVE_DASHBOARD_ROUTES,
  LIVE_DASHBOARD_ROUTES,
} from "@/pages/live/eventWorkspaceNavigation";

const DashboardEditPage = lazyRoute(() => import("@/pages/workspace/DashboardEditPage"));
const WorkspaceImportPage = lazyRoute(() => import("@/pages/workspace/WorkspaceImportPage"));
const UserProfilesPage = lazyRoute(() => import("@/pages/admin/UserProfilesPage"));
const UserDetailPage = lazyRoute(() => import("@/pages/admin/UserDetailPage"));
const TenantDetailPage = lazyRoute(() => import("@/pages/admin/TenantDetailPage"));
const TenantCreatePage = lazyRoute(() => import("@/pages/admin/TenantCreatePage"));
const WidgetCatalogPage = lazyRoute(() => import("@/pages/admin/WidgetCatalogPage"));
const NetworkIdListPage = lazyRoute(() => import("@/pages/admin/NetworkIdListPage"));
const NetworkIdCreatePage = lazyRoute(() => import("@/pages/admin/NetworkIdCreatePage"));
const NetworkIdDetailPage = lazyRoute(() => import("@/pages/admin/NetworkIdDetailPage"));
const EngineListPage = lazyRoute(() => import("@/pages/admin/EngineListPage"));
const EngineCreatePage = lazyRoute(() => import("@/pages/admin/EngineCreatePage"));
const EngineDetailPage = lazyRoute(() => import("@/pages/admin/EngineDetailPage"));
const ConfigurationPage = lazyRoute(() => import("@/pages/admin/ConfigurationPage"));
const DataTransferCreatePage = lazyRoute(
  () => import("@/pages/admin/configuration/DataTransferCreatePage"),
);
const DataTransferDetailPage = lazyRoute(
  () => import("@/pages/admin/configuration/DataTransferDetailPage"),
);
const WidgetCreatePage = lazyRoute(() => import("@/pages/admin/WidgetCreatePage"));
const WidgetDetailPage = lazyRoute(() => import("@/pages/admin/WidgetDetailPage"));
const WidgetEditPage = lazyRoute(() =>
  import("@/pages/admin/WidgetDetailPage").then((module) => ({ default: module.WidgetEditPage })),
);
const InvestigationsPage = lazyRoute(() => import("@/pages/investigations/InvestigationsPage"));
const OperationsPage = lazyRoute(() => import("@/pages/operations/Minerva/OperationsPage"));
const OperationsOverviewPage = lazyRoute(
  () => import("@/pages/operations/Minerva/OperationsOverviewPage"),
);
const JournalPage = lazyRoute(() =>
  import("./pages/journal/JournalPage").then((module) => ({
    default: module.JournalPage,
  })),
);

const StoredDashboardPage = lazyRoute(() =>
  import("./pages/live/StoredDashboardPage").then((module) => ({
    default: module.StoredDashboardPage,
  })),
);
const ReportsListPage = lazyRoute(() =>
  import("./pages/measurement/ReportsListPage").then((module) => ({
    default: module.ReportsListPage,
  })),
);
const ReportDashboardPage = lazyRoute(() =>
  import("./pages/measurement/ReportDashboardPage").then((module) => ({
    default: module.ReportDashboardPage,
  })),
);
const NewReportPage = lazyRoute(() =>
  import("./pages/measurement/NewReportPage").then((module) => ({
    default: module.NewReportPage,
  })),
);
/** Old flat campaign/event links, redirected onto the nested route they now live at. */
function LegacyCampaignRedirect() {
  const { campaignId = "" } = useParams();
  return <Navigate to={`/measurement/${DEFAULT_REPORT_ID}/campaigns/${campaignId}`} replace />;
}

function LegacyProtectedEventRedirect() {
  const { protectedEventId = "" } = useParams();
  return <Navigate to={`/measurement/${DEFAULT_REPORT_ID}/events/${protectedEventId}`} replace />;
}

function NotFoundPage() {
  useDocumentTitle("Page not found");
  return (
    <FullPageState
      code="404"
      title="Page not found"
      description="The page you are looking for does not exist."
      action={{ label: "Back to Home", href: "/home" }}
    />
  );
}

function ForbiddenPage() {
  useDocumentTitle("Access forbidden");
  return (
    <FullPageState
      code="403"
      title="Access forbidden"
      description="You are not authorized to access this section."
      action={{ label: "Back to Home", href: "/home" }}
    />
  );
}

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : "An unexpected error occurred.",
    };
  }

  render() {
    if (this.state.hasError) {
      return (
        <>
          <DocumentTitle title="Error" />
          <FullPageState
            title="Something went wrong"
            description={this.state.message}
            action={{ label: "Reload", onClick: () => window.location.reload() }}
          />
        </>
      );
    }
    return this.props.children;
  }
}

function RouteFallback() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/workspace/dashboards") || pathname.startsWith("/live"))
    return <DashboardRouteState />;
  return <FullPageLoader />;
}

function ProtectedRoute({
  children,
  sectionId,
}: {
  children: React.ReactNode;
  sectionId?: SectionId;
}) {
  const auth = useAuth();
  const authorization = useAuthorization();
  const access = resolveRouteAccess({
    isLoading: isBlockingAuthentication(auth) || authorization.isLoading,
    isAuthenticated: authorization.isAuthenticated,
    sectionId,
    canAccessSection: authorization.canAccessSection,
  });

  if (access === "loading") return <FullPageLoader />;
  if (access === "login") return <LoginRedirect />;
  if (authorization.error)
    return (
      <AccountLoadError
        title="Account access could not be loaded"
        error={authorization.error}
        onRetry={authorization.retry}
      />
    );
  // A client is not told that other spaces exist: what it cannot open simply is not found.
  if (sectionId && !authorization.isSectionAvailable(sectionId)) return <NotFoundPage />;
  if (access === "forbidden")
    return authorization.isClientProfile ? <NotFoundPage /> : <Navigate to="/forbidden" replace />;

  return <>{children}</>;
}

function AdminRoute({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const account = useAccount();
  if (isBlockingAuthentication(auth)) return <FullPageLoader />;
  if (!auth.isAuthenticated) return <LoginRedirect />;
  if (account.isPending) return <FullPageLoader label="Loading account" />;
  if (account.isError)
    return (
      <AccountLoadError
        title="Account could not be loaded"
        error={account.error}
        onRetry={() => void account.refetch()}
      />
    );
  if (account.data.profile !== "nagra-admin") return <Navigate to="/forbidden" replace />;
  return <ProtectedRoute sectionId="admin">{children}</ProtectedRoute>;
}

/** Sends to sign-in and keeps the page asked for, to come back to it afterwards. */
function LoginRedirect() {
  const location = useLocation();
  const state: ReturnState = { returnTo: safeReturnTo(pathOf(location)) };
  return <Navigate to="/login" replace state={state} />;
}

function AuthCallbackPage() {
  useDocumentTitle("Signing in");
  const auth = useAuth();
  const { audience } = useWorkspaceAudience();
  if (!auth.isLoading && auth.isAuthenticated)
    return (
      <Navigate
        to={returnToFromState(auth.user?.state) ?? getWorkspaceLandingPath(audience)}
        replace
      />
    );
  if (!auth.isLoading && auth.error) return <Navigate to="/login" replace />;
  return <FullPageLoader label="Signing in" />;
}

function WorkspaceLandingRedirect() {
  const { audience } = useWorkspaceAudience();
  return <Navigate to={getWorkspaceLandingPath(audience)} replace />;
}

function SectionLandingRedirect({ sectionId }: { sectionId: SectionId }) {
  const groups = useAuthorizedNavGroups();
  const group = groups.find((item) => item.id === sectionId);
  return <Navigate to={group?.defaultHref ?? group?.href ?? "/home"} replace />;
}

function AccountSetupBoundary({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const setup = useAccountSetup();
  const { pathname } = useLocation();

  if (
    !auth.isLoading &&
    auth.isAuthenticated &&
    pathname !== "/login" &&
    pathname !== "/postlogin"
  ) {
    if (setup.isPending) return <FullPageLoader label="Loading account" />;
    if (setup.error) {
      return (
        <AccountLoadError
          title="Account access could not be loaded"
          error={setup.error}
          onRetry={setup.retry}
        />
      );
    }
    if (setup.needsSetup && pathname !== "/home") return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <AppErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <AccountSetupBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/postlogin" element={<AuthCallbackPage />} />
            <Route path="/" element={<WorkspaceLandingRedirect />} />
            <Route
              path="/forbidden"
              element={
                <ProtectedRoute>
                  <ForbiddenPage />
                </ProtectedRoute>
              }
            />
            {/* Home */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            {/* Journal */}
            <Route
              path="/journal"
              element={
                <ProtectedRoute sectionId="journal">
                  <JournalPage />
                </ProtectedRoute>
              }
            />
            {/* Operations */}
            <Route
              path="/operations"
              element={
                <ProtectedRoute sectionId="operations">
                  <SectionLandingRedirect sectionId="operations" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/home"
              element={
                <ProtectedRoute sectionId="operations">
                  <Navigate to="/operations/overview" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/overview"
              element={
                <ProtectedRoute sectionId="operations">
                  <OperationsOverviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/:view"
              element={
                <ProtectedRoute sectionId="operations">
                  <OperationsPage />
                </ProtectedRoute>
              }
            />
            {/* Investigations */}
            <Route
              path="/investigations"
              element={
                <ProtectedRoute sectionId="investigations">
                  <InvestigationsPage />
                </ProtectedRoute>
              }
            />
            {/* Analytics */}
            <Route
              path="/live"
              element={
                <ProtectedRoute sectionId="live">
                  <StoredDashboardPage dashboardSlug="event-overview" />
                </ProtectedRoute>
              }
            />
            {[...LIVE_DASHBOARD_ROUTES, ...LEGACY_LIVE_DASHBOARD_ROUTES].map(
              ({ path, dashboardSlug }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute sectionId="live">
                      <StoredDashboardPage dashboardSlug={dashboardSlug} />
                    </ProtectedRoute>
                  }
                />
              ),
            )}
            <Route
              path="/live/dashboards/:slug"
              element={
                <ProtectedRoute sectionId="live">
                  <StoredDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live/:slug"
              element={
                <ProtectedRoute sectionId="live">
                  <StoredDashboardPage requireLivePlacement />
                </ProtectedRoute>
              }
            />
            {/* Workspace */}
            <Route
              path="/workspace"
              element={
                <ProtectedRoute sectionId="workspace">
                  <Navigate to="/workspace/dashboards" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspace/dashboards"
              element={
                <ProtectedRoute sectionId="workspace">
                  <DashboardListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspace/import"
              element={
                <AdminRoute>
                  <WorkspaceImportPage />
                </AdminRoute>
              }
            />
            {/*
             * The editor and its preview are two addresses of one page: the parent
             * keeps the editor mounted (unsaved edits, undo history) while the browser's
             * Back and Forward move between composing and previewing. (The editor
             * itself starts afresh for another dashboard: it is keyed by its id.)
             */}
            <Route
              element={
                <ProtectedRoute sectionId="dashboards">
                  <DashboardEditPage />
                </ProtectedRoute>
              }
            >
              <Route path="/workspace/dashboards/new" />
              <Route path="/workspace/dashboards/new/preview" />
              <Route path="/workspace/dashboards/:id/edit" />
              <Route path="/workspace/dashboards/:id/preview" />
            </Route>
            <Route
              path="/workspace/dashboards/:slug"
              element={
                <ProtectedRoute sectionId="workspace">
                  <StoredDashboardPage />
                </ProtectedRoute>
              }
            />
            {/* Reports */}
            <Route
              path="/measurement"
              element={
                <ProtectedRoute sectionId="measurement">
                  <ReportsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/measurement/dashboards/:slug"
              element={
                <ProtectedRoute sectionId="measurement">
                  <StoredDashboardPage />
                </ProtectedRoute>
              }
            />
            {/*
            The "new report" journey (a literal segment, ranked above ":reportId").
          */}
            <Route
              path="/measurement/new"
              element={
                <ProtectedRoute sectionId="measurement">
                  <AdminRoute>
                    <NewReportPage />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            {/*
            Every page of every generated report: ReportDashboardPage resolves
            the edition (latest, or ?edition=<slug>), picks the dashboard
            template of the requested view from the report's own template set
            and renders it with the edition as context — so a new report or a
            new edition needs no route. Drill-downs are nested under their
            parent so the report (and campaign) never drop out of the URL or
            the breadcrumb. React Router ranks routes by literal segments, not
            declaration order.
          */}
            <Route
              path="/measurement/:reportId"
              element={
                <ProtectedRoute sectionId="measurement">
                  <ReportDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/measurement/:reportId/campaigns/:campaignId"
              element={
                <ProtectedRoute sectionId="measurement">
                  <ReportDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/measurement/:reportId/campaigns/:campaignId/events/:protectedEventId"
              element={
                <ProtectedRoute sectionId="measurement">
                  <ReportDashboardPage />
                </ProtectedRoute>
              }
            />
            {/*
            An event reached without its campaign (e.g. from a campaign report's
            root page): the event's own data names its campaign for the breadcrumb.
          */}
            <Route
              path="/measurement/:reportId/events/:protectedEventId"
              element={
                <ProtectedRoute sectionId="measurement">
                  <ReportDashboardPage />
                </ProtectedRoute>
              }
            />
            {/*
            Legacy flat links (pre-nesting): redirect onto the "protection-overview"
            report so old bookmarks/shares still resolve instead of 404ing.
          */}
            <Route path="/measurement/campaigns/:campaignId" element={<LegacyCampaignRedirect />} />
            <Route
              path="/measurement/events/:protectedEventId"
              element={<LegacyProtectedEventRedirect />}
            />
            {/* Configure */}
            <Route
              path="/admin/data-transfer/new/:operation"
              element={
                <AdminRoute>
                  <DataTransferCreatePage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/data-transfer/:transferId"
              element={
                <AdminRoute>
                  <DataTransferDetailPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/data-transfer"
              element={
                <AdminRoute>
                  <ConfigurationPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/widgets"
              element={
                <AdminRoute>
                  <WidgetCatalogPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/widgets/new"
              element={
                <AdminRoute>
                  <WidgetCreatePage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/widgets/:widgetId"
              element={
                <AdminRoute>
                  <WidgetDetailPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/widgets/:widgetId/edit"
              element={
                <AdminRoute>
                  <WidgetEditPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/widget-catalog"
              element={<Navigate to="/admin/widgets" replace />}
            />
            <Route
              path="/admin/network-ids"
              element={
                <AdminRoute>
                  <NetworkIdListPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/network-ids/new"
              element={
                <AdminRoute>
                  <NetworkIdCreatePage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/network-ids/:networkIdEntry"
              element={
                <AdminRoute>
                  <NetworkIdDetailPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute sectionId="admin">
                  <Navigate to="/admin/tenants" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tenants"
              element={
                <ProtectedRoute sectionId="admin">
                  <TenantListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tenants/new"
              element={
                <AdminRoute>
                  <TenantCreatePage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/engines"
              element={
                <AdminRoute>
                  <EngineListPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/engines/new"
              element={
                <AdminRoute>
                  <EngineCreatePage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/engines/:engineId"
              element={
                <AdminRoute>
                  <EngineDetailPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/tenants/:tenantId"
              element={
                <ProtectedRoute sectionId="admin">
                  <TenantDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/user-profiles"
              element={
                <AdminRoute>
                  <UserProfilesPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/user-profiles/:accountId"
              element={
                <AdminRoute>
                  <UserDetailPage />
                </AdminRoute>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AccountSetupBoundary>
      </Suspense>
    </AppErrorBoundary>
  );
}
